import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Typography,
  Paper,
  Chip,
  Divider,
  TextField,
  IconButton,
  Card,
  CardContent,
  CardActionArea,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { getEmailTemplateCategoryOptions } from '../../../utils/Enums';

const CreateTemplateModal = ({ open, onClose, onSave, allTemplates = [] }) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templatePageIndex, setTemplatePageIndex] = useState(0);
  const [editorTab, setEditorTab] = useState(0); // 0: HTML Code, 1: Preview
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    category: '',
    htmlContent: ''
  });

  // Separar templates del sistema y personalizados
  const systemTemplates = React.useMemo(() => {
    return allTemplates.filter(t => t.isSystem === true);
  }, [allTemplates]);

  const customTemplates = React.useMemo(() => {
    return allTemplates.filter(t => !t.isSystem);
  }, [allTemplates]);

  // Combinar todas las opciones para el viewpager
  const allTemplateOptions = React.useMemo(() => {
    const options = [
      { id: '', name: t('start_from_scratch'), category: '', subject: '', htmlContent: '', isSystem: false, isStartFromScratch: true }
    ];
    return [...options, ...systemTemplates, ...customTemplates];
  }, [systemTemplates, customTemplates, t]);

  // Calcular cuántas páginas hay (3 templates por página)
  const templatesPerPage = 3;
  const totalPages = Math.ceil(allTemplateOptions.length / templatesPerPage);

  // Obtener los templates de la página actual
  const getCurrentPageTemplates = () => {
    const startIndex = templatePageIndex * templatesPerPage;
    const endIndex = startIndex + templatesPerPage;
    return allTemplateOptions.slice(startIndex, endIndex);
  };

  // Resetear cuando se abre el modal
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setSelectedTemplateId('');
      setTemplatePageIndex(0);
      setFormData({
        name: '',
        subject: '',
        category: '',
        htmlContent: ''
      });
    }
  }, [open]);

  const handleNextTemplate = () => {
    setTemplatePageIndex((prevIndex) => 
      prevIndex < totalPages - 1 ? prevIndex + 1 : prevIndex
    );
  };

  const handleBackTemplate = () => {
    setTemplatePageIndex((prevIndex) => 
      prevIndex > 0 ? prevIndex - 1 : prevIndex
    );
  };

  const currentPageTemplates = getCurrentPageTemplates();

  // Cuando se selecciona una plantilla, cargar su contenido
  useEffect(() => {
    if (selectedTemplateId && selectedTemplateId !== '') {
      // Buscar el template tanto por ID numérico como por string
      const templateId = isNaN(selectedTemplateId) ? selectedTemplateId : parseInt(selectedTemplateId);
      const selectedTemplate = allTemplates.find(t => 
        t.id === templateId || String(t.id) === String(selectedTemplateId)
      );
      if (selectedTemplate) {
        console.log('Template encontrado:', selectedTemplate);
        setFormData({
          name: `${selectedTemplate.name} (Copia)`,
          subject: selectedTemplate.subject || '',
          category: selectedTemplate.category || '',
          htmlContent: selectedTemplate.htmlContent || ''
        });
      } else {
        console.log('Template no encontrado. ID buscado:', templateId, 'All templates:', allTemplates);
      }
    } else if (selectedTemplateId === '') {
      // Si se selecciona "empezar desde cero", resetear
      setFormData({
        name: '',
        subject: '',
        category: '',
        htmlContent: ''
      });
    }
  }, [selectedTemplateId, allTemplates]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  const selectedTemplate = selectedTemplateId && selectedTemplateId !== ''
    ? allTemplates.find(t => String(t.id) === String(selectedTemplateId))
    : null;

  const steps = [t('select_template'), t('edit_template')];

  const handleClose = (event, reason) => {
    // Solo permitir cerrar con botones o ESC, no con backdrop click
    if (reason === 'backdropClick') {
      // Verificar si hay un menú abierto en el DOM
      const menuElement = document.querySelector('.MuiMenu-paper, .MuiPopover-paper');
      if (menuElement) {
        // Si hay un menú abierto, no cerrar el Dialog
        return;
      }
      return;
    }
    setActiveStep(0);
    setSelectedTemplateId('');
    setFormData({
      name: '',
      subject: '',
      category: '',
      htmlContent: ''
    });
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getIcon('add')}
          {t('create_template')}
        </Box>
        <IconButton 
          onClick={handleClose} 
          size="small"
          sx={{
            width: 40,
            height: 40,
            minWidth: 40,
            minHeight: 40,
            maxWidth: 40,
            maxHeight: 40,
            padding: 0,
            borderRadius: '50%',
            '&:hover': {
              borderRadius: '50%'
            }
          }}
        >
          {getIcon('close')}
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2, mb: 3 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Paso 1: Seleccionar plantilla - ViewPager con 3 opciones por página */}
        {activeStep === 0 && currentPageTemplates.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('select_template_to_clone')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('select_template_help')} - Página {templatePageIndex + 1} de {totalPages}
            </Typography>

            {/* ViewPager Container */}
            <Box sx={{ position: 'relative', mb: 3 }}>
              {/* Botón Anterior */}
              <IconButton
                onClick={handleBackTemplate}
                disabled={templatePageIndex === 0}
                sx={{
                  position: 'absolute',
                  left: -16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  bgcolor: 'background.paper',
                  boxShadow: 2,
                  '&:hover': {
                    bgcolor: 'background.paper',
                  },
                  '&.Mui-disabled': {
                    opacity: 0.3
                  }
                }}
              >
                {getIcon('chevronBack')}
              </IconButton>

              {/* Grid de 3 cards */}
              <Grid container spacing={2}>
                {currentPageTemplates.map((template) => (
                  <Grid item xs={12} md={4} key={template.id || 'start-from-scratch'}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        border: selectedTemplateId === String(template.id || '') ? 2 : 1,
                        borderColor: selectedTemplateId === String(template.id || '') ? 'primary.main' : 'divider',
                        transition: 'all 0.3s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 4,
                          transform: 'translateY(-4px)'
                        }
                      }}
                      onClick={() => setSelectedTemplateId(String(template.id || ''))}
                    >
                      <CardContent sx={{ p: 2, flex: 1 }}>
                        {/* Header */}
                        <Typography variant="h6" sx={{ mb: 1, wordBreak: 'break-word' }}>
                          {template.name}
                        </Typography>

                        {!template.isStartFromScratch && (
                          <>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                              {template.category || '-'} • {template.subject || '-'}
                            </Typography>

                            {/* Preview del HTML - más pequeño */}
                            <Paper
                              sx={{
                                p: 1.5,
                                bgcolor: 'white',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                maxHeight: 200,
                                overflow: 'auto',
                                minHeight: 180,
                                '& *': {
                                  fontSize: '11px !important',
                                  lineHeight: '1.3 !important',
                                },
                                '& h1, & h2, & h3': {
                                  fontSize: '12px !important',
                                  margin: '4px 0 !important',
                                },
                                '& p': {
                                  margin: '2px 0 !important',
                                }
                              }}
                            >
                              <Box
                                sx={{
                                  '& *': {
                                    maxWidth: '100%',
                                  }
                                }}
                                dangerouslySetInnerHTML={{ __html: template.htmlContent }}
                              />
                            </Paper>
                          </>
                        )}

                        {template.isStartFromScratch && (
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body1" color="text.secondary" gutterBottom>
                              {t('start_from_scratch')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Crear desde cero
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                      
                      {/* Chip abajo de la card */}
                      <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                        {template.isStartFromScratch ? (
                          <Chip label="Vacío" size="small" color="default" variant="outlined" />
                        ) : template.isSystem ? (
                          <Chip label="Sistema" size="small" color="info" variant="outlined" />
                        ) : (
                          <Chip label="Personalizado" size="small" color="secondary" variant="outlined" />
                        )}
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Botón Siguiente */}
              <IconButton
                onClick={handleNextTemplate}
                disabled={templatePageIndex === totalPages - 1}
                sx={{
                  position: 'absolute',
                  right: -16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  bgcolor: 'background.paper',
                  boxShadow: 2,
                  '&:hover': {
                    bgcolor: 'background.paper',
                  },
                  '&.Mui-disabled': {
                    opacity: 0.3
                  }
                }}
              >
                {getIcon('chevronForward')}
              </IconButton>
            </Box>

            {/* Indicadores de página (dots) */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
              {Array.from({ length: totalPages }).map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setTemplatePageIndex(index)}
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: index === templatePageIndex ? 'primary.main' : 'grey.400',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      bgcolor: index === templatePageIndex ? 'primary.dark' : 'grey.600',
                      transform: 'scale(1.2)'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Paso 2: Editar plantilla */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('edit_template_details')}
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('name')}
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('category')}</InputLabel>
                  <Select
                    value={formData.category}
                    label={t('category')}
                    onChange={(e) => handleChange('category', e.target.value)}
                  >
                    <MenuItem value="">
                      <em>{t('select_category') || 'Seleccionar categoría'}</em>
                    </MenuItem>
                    {getEmailTemplateCategoryOptions().map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('subject')}
                  value={formData.subject}
                  onChange={(e) => handleChange('subject', e.target.value)}
                  required
                  placeholder={t('email_subject_placeholder')}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs value={editorTab} onChange={(e, newValue) => setEditorTab(newValue)}>
                    <Tab label={t('html_code')} />
                    <Tab label={t('preview')} />
                  </Tabs>
                </Box>

                {/* Nota sobre variables */}
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'info.light', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
                  <Typography variant="caption" sx={{ display: 'block', color: 'info.dark' }}>
                    <strong>Variables disponibles:</strong> {'{{clientName}}'}, {'{{clientEmail}}'}, {'{{companyName}}'}, {'{{currentDate}}'}. La opción para cancelar la suscripción se agrega automáticamente a todos los emails de marketing.
                  </Typography>
                </Box>

                {/* Tab 0: HTML Code Editor */}
                {editorTab === 0 && (
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={20}
                      label={t('html_content')}
                      value={formData.htmlContent}
                      onChange={(e) => handleChange('htmlContent', e.target.value)}
                      placeholder={t('html_content_placeholder')}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontFamily: 'monospace',
                          fontSize: '0.875rem'
                        }
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {t('html_content_help')}
                    </Typography>
                  </Box>
                )}

                {/* Tab 1: Preview */}
                {editorTab === 1 && (
                  <Box>
                    <Paper
                      sx={{
                        p: 3,
                        minHeight: 450,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'white'
                      }}
                    >
                      <Typography variant="h6" gutterBottom>
                        {formData.subject || t('email_preview')}
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Box
                        dangerouslySetInnerHTML={{ __html: formData.htmlContent || t('no_content') }}
                        sx={{
                          '& img': { maxWidth: '100%', height: 'auto' },
                          '& a': { color: 'primary.main' },
                          '& *': { maxWidth: '100%' }
                        }}
                      />
                    </Paper>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          {t('cancel')}
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} variant="outlined">
            {t('back')}
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            onClick={handleNext}
            variant="contained"
          >
            {t('next')}
          </Button>
        ) : (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name || !formData.subject || !formData.htmlContent}
            startIcon={getIcon('checkmarkDone')}
          >
            {t('create')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateTemplateModal;
